import { Text } from '@shared/components';
import {
  AsyncTransferData,
  CallTransferAcceptedData,
  CallTransferredData,
  ConversationEventType,
  ReturnedToTransferredCallData,
  UnknownConversationEventData,
} from '@shared/generated/graphql';
import { ConversationEvent } from '@shared/types/conversation';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { assertUnreachable } from 'clerk_common/enum';
import { formatPhoneNumberWithExtension } from 'clerk_common/stringification/phone';
import clsx from 'clsx';
import { isNil } from 'lodash';
import {
  BsFillQuestionCircleFill,
  BsTelephoneForwardFill,
} from 'react-icons/bs';
import { LuPhoneOff, LuSave } from 'react-icons/lu';
import {
  PiPauseCircle,
  PiPhoneSlash,
  PiPhoneTransfer,
  PiProhibit,
} from 'react-icons/pi';
import { TbMailForward } from 'react-icons/tb';

export type ConversationEventData =
  | CallTransferredData
  | CallTransferAcceptedData
  | AsyncTransferData
  | ReturnedToTransferredCallData
  | UnknownConversationEventData;

const ROOT = makeRootClassName('ConversationEvent');
const el = makeElementClassNameFactory(ROOT);

type ActionExecutionEventProps = {
  event: ConversationEvent;
};

export const ConversationEventComponent = ({
  event,
}: ActionExecutionEventProps) => {
  return (
    <div className={clsx(el`ce-event`)}>
      <ConversationEventHeader event={event} />
      <ConversationEventContents event={event} />
    </div>
  );
};

type HeaderProps = { event: ConversationEvent };

export const ConversationEventHeader = ({ event }: HeaderProps) => {
  return (
    <div className="flex items-center gap-2">
      <span className="flex-shrink-0">
        <ConversationEventIcon event={event} />
      </span>
      <Text type="body-sm">{stringifyConversationEventName(event)}</Text>
    </div>
  );
};

export const ConversationEventIcon = ({
  event,
}: {
  event: ConversationEvent;
}) => {
  const name = event.name;
  switch (name) {
    case ConversationEventType.CALL_FAILED:
    case ConversationEventType.CALL_ENDED:
      return <LuPhoneOff size={12} />;
    case ConversationEventType.INTENT_TO_HANG_UP_REGISTERED:
      return <PiPhoneSlash size={12} />;
    case ConversationEventType.INTENT_TO_TRANSFER_REGISTERED:
      return <PiPhoneTransfer size={12} />;
    case ConversationEventType.INTENT_TO_HOLD_REGISTERED:
      return <PiPauseCircle size={12} />;
    case ConversationEventType.INTENT_TO_HANG_UP_CANCELLED:
    case ConversationEventType.INTENT_TO_TRANSFER_CANCELLED:
    case ConversationEventType.INTENT_TO_HOLD_CANCELLED:
      return <PiProhibit size={12} />;
    case ConversationEventType.CALL_TRANSFERRED:
    case ConversationEventType.CALL_TRANSFER_ACCEPTED:
    case ConversationEventType.CONFERENCE_JOINED:
    case ConversationEventType.RETURNED_TO_TRANSFERRED_CALL:
      return <BsTelephoneForwardFill size={12} />;
    case ConversationEventType.ASYNC_TRANSFER:
      return <TbMailForward size={12} />;
    case ConversationEventType.CALL_RECORDING_STARTED:
    case ConversationEventType.CALL_RECORDING_RECEIVED:
      return <LuSave size={12} />;
    case ConversationEventType.CHAT_VIEW_CONTEXT_UPDATED:
    case ConversationEventType.UNKNOWN:
    case ConversationEventType.CONVERSATION_CLOSED_BY_WATCHER:
      return <BsFillQuestionCircleFill />;
    default:
      return assertUnreachable(name);
  }
};

export const stringifyConversationEventName = (event: ConversationEvent) => {
  const name = event.name;
  switch (name) {
    case ConversationEventType.CALL_FAILED:
      return 'Call failed';
    case ConversationEventType.CALL_ENDED:
      return 'Call ended';
    case ConversationEventType.CALL_TRANSFERRED:
      return 'Call transferred';
    case ConversationEventType.CALL_TRANSFER_ACCEPTED:
      return 'Call transfer accepted';
    case ConversationEventType.ASYNC_TRANSFER:
      return 'Conversation transferred';
    case ConversationEventType.RETURNED_TO_TRANSFERRED_CALL:
      return 'Returned to transferred call';
    case ConversationEventType.CONFERENCE_JOINED:
      return 'Conference joined';
    case ConversationEventType.CALL_RECORDING_STARTED:
      return 'Call recording started';
    case ConversationEventType.CALL_RECORDING_RECEIVED:
      return 'Call recording saved';
    case ConversationEventType.CONVERSATION_CLOSED_BY_WATCHER:
      return 'Conversation closed by watcher';
    case ConversationEventType.CHAT_VIEW_CONTEXT_UPDATED:
      return 'Chat view context updated';
    case ConversationEventType.INTENT_TO_HANG_UP_REGISTERED:
      return 'Intent to hang up registered';
    case ConversationEventType.INTENT_TO_HANG_UP_CANCELLED:
      return 'Intent to hang up cancelled';
    case ConversationEventType.INTENT_TO_TRANSFER_REGISTERED:
      return 'Intent to transfer registered';
    case ConversationEventType.INTENT_TO_TRANSFER_CANCELLED:
      return 'Intent to transfer cancelled';
    case ConversationEventType.INTENT_TO_HOLD_REGISTERED:
      return 'Intent to hold registered';
    case ConversationEventType.INTENT_TO_HOLD_CANCELLED:
      return 'Intent to hold cancelled';
    case ConversationEventType.UNKNOWN:
      return 'Unknown event';
    default:
      return assertUnreachable(name);
  }
};

type ContentsProps = {
  event: ConversationEvent;
  isEditable?: boolean;
};

export const ConversationEventContents = ({ event }: ContentsProps) => {
  if (isNil(event.data)) return null;
  switch (event.name) {
    case ConversationEventType.UNKNOWN:
      return (
        <div>
          {event.id} {event.name}
        </div>
      );
    case ConversationEventType.CALL_TRANSFERRED:
      return <CallTransferredEventContents data={event.data} />;
    case ConversationEventType.CALL_TRANSFER_ACCEPTED:
      return <CallTransferAcceptedEventContents data={event.data} />;
    case ConversationEventType.ASYNC_TRANSFER:
      return <AsyncTransferEventContents data={event.data} />;
    case ConversationEventType.RETURNED_TO_TRANSFERRED_CALL:
      return <ReturnedToTransferredCallEventContents data={event.data} />;
    case ConversationEventType.CALL_FAILED:
    case ConversationEventType.CALL_ENDED:
    case ConversationEventType.CONFERENCE_JOINED:
    case ConversationEventType.CALL_RECORDING_STARTED:
    case ConversationEventType.CALL_RECORDING_RECEIVED:
    case ConversationEventType.CHAT_VIEW_CONTEXT_UPDATED:
    case ConversationEventType.CONVERSATION_CLOSED_BY_WATCHER:
    case ConversationEventType.INTENT_TO_HANG_UP_REGISTERED:
    case ConversationEventType.INTENT_TO_HANG_UP_CANCELLED:
    case ConversationEventType.INTENT_TO_TRANSFER_REGISTERED:
    case ConversationEventType.INTENT_TO_TRANSFER_CANCELLED:
    case ConversationEventType.INTENT_TO_HOLD_REGISTERED:
    case ConversationEventType.INTENT_TO_HOLD_CANCELLED:
      return null;
    default:
      return assertUnreachable(event.name);
  }
};

function isCallTransferredEventData(
  data: ConversationEventData
): data is CallTransferredData {
  return data.eventDataType === ConversationEventType.CALL_TRANSFERRED;
}

function CallTransferredEventContents({
  data,
}: {
  data: ConversationEventData;
}) {
  if (!isCallTransferredEventData(data)) return null;

  const { phoneNumber, extension } = data;

  return (
    <div className="flex flex-col gap-1 py-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">
          {`Transferred to ${formatPhoneNumberWithExtension(
            phoneNumber,
            extension ?? undefined
          )}`}
        </Text>
      </pre>
    </div>
  );
}

function isCallTransferAcceptedEventData(
  data: ConversationEventData
): data is CallTransferAcceptedData {
  return data.eventDataType === ConversationEventType.CALL_TRANSFER_ACCEPTED;
}

function CallTransferAcceptedEventContents({
  data,
}: {
  data: ConversationEventData;
}) {
  if (!isCallTransferAcceptedEventData(data)) return null;

  const { conferenceId, transferCallSid } = data;

  return (
    <div className="flex flex-col gap-1 py-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">Conference: {conferenceId}</Text>
        <Text type="body-xs">Transfer call SID: {transferCallSid}</Text>
      </pre>
    </div>
  );
}

function isAsyncTransferEventData(
  data: ConversationEventData
): data is AsyncTransferData {
  return data.eventDataType === ConversationEventType.ASYNC_TRANSFER;
}

function AsyncTransferEventContents({ data }: { data: ConversationEventData }) {
  if (!isAsyncTransferEventData(data)) return null;

  const { ccRecipients } = data;

  return (
    <div className="flex flex-col gap-1 py-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">CC: {ccRecipients.join(', ')}</Text>
      </pre>
    </div>
  );
}

function isReturnedToTransferredCallEventData(
  data: ConversationEventData
): data is ReturnedToTransferredCallData {
  return (
    data.eventDataType === ConversationEventType.RETURNED_TO_TRANSFERRED_CALL
  );
}
function ReturnedToTransferredCallEventContents({
  data,
}: {
  data: ConversationEventData;
}) {
  if (!isReturnedToTransferredCallEventData(data)) return null;

  const { conferenceId, transferAttemptConversationId } = data;

  return (
    <div className="flex flex-col gap-1 py-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">Conference: {conferenceId}</Text>
        <Text type="body-xs">
          Transfer attempt conversation: {transferAttemptConversationId}
        </Text>
      </pre>
    </div>
  );
}
